// Quasar's global stylesheet MUST be the first CSS this app pulls in.
//
// PRODUCT-26110: CSS lands in the document in module-evaluation order, so whichever
// sheet is emitted last wins every specificity tie. Importing Quasar late (as this file
// used to, at the bottom) put its global sheet after all of ours and handed it every
// tie -- e.g. `.disabled, [disabled] { opacity: .6 !important }` beating
// `.NewButton--disabled { opacity: .3 !important }`, and its `.q-field` defaults
// beating our colour utilities. It has to sit above the app imports too, not just above
// the stylesheet imports: `@/createInsuranceApplication` and the view components below
// pull in their own component styles as they evaluate. The webpack build this replaced
// emitted Quasar first, which is why dev renders the app's values.
// eslint-disable-next-line import-x/order
import { installQuasar } from './quasar';

// Initialize Sentry
import Sentry, { attachSentryToVueApp } from '@/sentry';

// Load polyfills
import 'blueimp-canvas-to-blob';

// Load Vue and its dependent plugins
import { installFloatingVue } from '@evidentid/dashboard-commons/components/Tooltip';

// Load the application factory
import { createInsuranceApplication } from '@/createInsuranceApplication';

// Load application requirements
import ErrorComponent from '@/views/Error.vue';
import LoadingComponent from '@/views/Loading.vue';
import LoginComponent from '@/views/Login.vue';
import RegisterComponent from '@/views/Register.vue';
import ResetPasswordComponent from '@/views/ResetPassword.vue';
import TermsComponent from '@/views/Terms.vue';
import VerifyEmailComponent from '@/views/VerifyEmail.vue';

// Load the configuration
import { config } from '@/config';
import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
import { useWcJurisdictionsStore } from '@/modules/entity-details/wcJurisdictions.store';

// Load stylesheets (Quasar's own sheet is imported at the top of this file -- see there)
import './styles/main.scss';
import './styles/tailwind.css';
import {
    mountAppToRootElement,
    partnerCustomizationSetup,
    watchRpChangeAndLoadCategorizedEnumLabels,
    watchRpChangeAndResumeContractExtractionPolling,
    watchRpChangeLoadUserRolesAndTryParseRbacFeatures,
    watchUserRolesChangesAndTryParseRbacFeatures,
    watchUserTokenRpRoleMapChangesAndTryParseRbacFeatures,
} from './mainHelpers';

// Set-up initialization
async function main() {
    // Set-up views
    const views: Parameters<typeof createInsuranceApplication>[0]['views'] = {
        Error: ErrorComponent,
        Loading: LoadingComponent,
        Login: LoginComponent,
        Register: RegisterComponent,
        ResetPassword: ResetPasswordComponent,
        VerifyEmail: VerifyEmailComponent,
        Terms: TermsComponent,
    };

    // Set-up application
    const app = await createInsuranceApplication({
        ...config,
        Sentry,
        views,
        // PRODUCT-26110 Phase 2B2 Task 1 (Quasar 2 / floating-vue): both installed
        // against the real `createApp()` instance inside Application.mount() -- see
        // quasar.ts and dashboard-commons' Tooltip/createTooltipPlugin.ts.
        appPlugins: [ installQuasar, installFloatingVue, attachSentryToVueApp ],
    });

    partnerCustomizationSetup(app);
    mountAppToRootElement(app);
    watchRpChangeAndLoadCategorizedEnumLabels(app);
    watchRpChangeAndResumeContractExtractionPolling(app);
    watchRpChangeLoadUserRolesAndTryParseRbacFeatures(app);
    watchUserTokenRpRoleMapChangesAndTryParseRbacFeatures(app);
    watchUserRolesChangesAndTryParseRbacFeatures(app);
    useDashboardStore().loadRiskRequirementDefinitions();
    useDashboardStore().loadCredentialTypes();
    useWcJurisdictionsStore().load();
    return app;
}

// Run app initialization
main().then(
    (app) => {
        // Attach application instance to window for development
        if (import.meta.env.DEV) {
            // eslint-disable-next-line
            // @ts-ignore: attaching application instance to window
            window.insuranceApp = app;
        }
    },
    (error) => {
        setTimeout(() => {
            throw error;
        });
    },
);
